import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UtensilsCrossed, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackDiscovery } from '../utils/analytics';
import { sectionsFor, menuCategoryNames, itemsInCategory, isAppOnlyCategory } from '../utils/menuSections';
import { useMenuLayout } from '../hooks/useMenuLayout';

/**
 * FAB that opens a categorized menu popup; hides when within 80 px of page bottom.
 * @param {Function} onCategorySelect - if omitted, navigates to /menu?category= instead
 */
export default function FloatingMenuButton({ menuItems = [], activeCategory = null, onCategorySelect = null, onSubCategorySelect = null }) {
  const [open, setOpen] = useState(false);
  const { sections: layoutSections } = useMenuLayout();
  const [nearBottom, setNearBottom] = useState(false);
  const [scrollHidden, setScrollHidden] = useState(false);
  const navigate = useNavigate();

  // Hide on scroll-down (so the FAB doesn't sit on top of cards), reveal on
  // scroll-up or when scrolling stops. Also hide when within 80px of the page
  // bottom so it doesn't overlap the last item/text.
  useEffect(() => {
    let lastY = window.scrollY || window.pageYOffset;
    let stopTimer = null;

    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const viewport = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = fullHeight - (scrollY + viewport);
      setNearBottom(distanceFromBottom < 80);

      const delta = scrollY - lastY;
      if (delta > 4 && scrollY > 80) {
        setScrollHidden(true);
      } else if (delta < -4) {
        setScrollHidden(false);
      }
      lastY = scrollY;

      if (stopTimer) clearTimeout(stopTimer);
      stopTimer = setTimeout(() => setScrollHidden(false), 600);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (stopTimer) clearTimeout(stopTimer);
    };
  }, [menuItems.length]);

  // Own categories plus app-only ones (Business Rule #62, e.g. "Signature Drinks").
  const categories = menuCategoryNames(menuItems);

  const categoryCounts = categories.reduce((acc, cat) => {
    acc[cat] = itemsInCategory(menuItems, cat).length;
    return acc;
  }, {});

  // An app-only category lists flat on the Menu page, so it offers no sub-categories.
  const subCategoryCounts = categories.reduce((acc, cat) => {
    const subs = {};
    if (isAppOnlyCategory(menuItems, cat)) return acc;
    menuItems.filter(i => i.category === cat).forEach(i => {
      const sub = i.sub_category || '';
      if (sub) subs[sub] = (subs[sub] || 0) + 1;
    });
    if (Object.keys(subs).length > 0) acc[cat] = subs;
    return acc;
  }, {});

  const activeSections = sectionsFor(categories, layoutSections).filter(s => s.categories.some(c => categories.includes(c)));

  const handleCategoryClick = (cat) => {
    setOpen(false);
    trackDiscovery('menu_fab', cat);
    if (onCategorySelect) {
      onCategorySelect(cat);
    } else {
      navigate(`/menu?category=${encodeURIComponent(cat)}`);
    }
  };

  if (menuItems.length === 0) return null;

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[60]"
            onClick={() => setOpen(false)}
            data-testid="menu-popup-backdrop"
          />
        )}
      </AnimatePresence>

      {/* Category Popup */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed left-4 right-4 lg:left-auto lg:right-[calc(50%-23rem)] lg:w-[26rem] z-[70] bg-card rounded-2xl shadow-2xl max-h-[60vh] overflow-y-auto border bottom-[calc(env(safe-area-inset-bottom)+8.5rem)]"
            data-testid="menu-popup"
          >
            <div className="p-4">
              {activeSections.map(section => (
                <div key={section.name} className="mb-3 last:mb-0">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">{section.name}</p>
                  {section.categories.filter(c => categories.includes(c)).map(cat => (
                    <PopupRow
                      key={cat}
                      cat={cat}
                      count={categoryCounts[cat] || 0}
                      isActive={activeCategory === cat}
                      subCounts={subCategoryCounts[cat]}
                      onClick={() => handleCategoryClick(cat)}
                      onSubClick={(sub) => {
                        setOpen(false);
                        if (onSubCategorySelect) {
                          onSubCategorySelect(cat, sub);
                        } else {
                          handleCategoryClick(cat);
                        }
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button — hides on scroll-down and near page bottom so it
          doesn't cover content. Always visible while popup is open. Sits
          just above the bottom nav (6rem offset) and uses tight padding so
          the right edge stays narrow enough to clear adjacent card content
          on 320–375px viewports. */}
      <AnimatePresence>
        {((!nearBottom && !scrollHidden) || open) && (
          <motion.button
            key="floating-menu-btn"
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{ duration: 0.18 }}
            onClick={() => setOpen(!open)}
            className="fixed right-4 lg:right-[calc(50%-23rem)] z-[45] flex items-center gap-1.5 bg-foreground text-background px-3 py-2 rounded-full shadow-lg hover:shadow-xl transition-shadow bottom-[calc(env(safe-area-inset-bottom)+6rem)]"
            data-testid="floating-menu-btn"
          >
            {open ? (
              <><X className="w-4 h-4" /><span className="text-sm font-bold">Close</span></>
            ) : (
              <><UtensilsCrossed className="w-4 h-4" /><span className="text-sm font-bold">Menu</span></>
            )}
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}

function PopupRow({ cat, count, isActive, subCounts, onClick, onSubClick }) {
  const hasSubs = subCounts && Object.keys(subCounts).length > 0;
  const [expanded, setExpanded] = useState(hasSubs);

  return (
    <div>
      <button
        onClick={onClick}
        className={`flex items-center justify-between w-full px-3 py-2.5 rounded-xl text-left transition-colors ${
          isActive ? 'bg-accent/10 text-accent' : 'hover:bg-muted'
        }`}
        data-testid={`popup-cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span className="flex items-center gap-1.5">
          <span className={`text-sm ${isActive ? 'font-bold' : 'font-medium'}`}>{cat}</span>
          {hasSubs && (
            <span
              role="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(!expanded); }}
              className={`text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full transition-colors ${
                expanded ? 'text-red-500' : 'text-accent'
              }`}
              data-testid={`popup-expand-${cat.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {expanded ? '−' : '+'}
            </span>
          )}
        </span>
        <span className={`text-sm tabular-nums ${isActive ? 'font-bold text-accent' : 'text-muted-foreground'}`}>{count}</span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && hasSubs && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {Object.entries(subCounts).map(([sub, subCount]) => (
              <button
                key={sub}
                onClick={() => onSubClick(sub)}
                className="flex items-center justify-between w-full pl-9 pr-3 py-2 text-left hover:bg-muted/50 rounded-lg transition-colors"
                data-testid={`popup-sub-${sub.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="text-sm text-muted-foreground">{sub}</span>
                <span className="text-sm text-muted-foreground tabular-nums">{subCount}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
